import { useEffect, useState } from "react";
import api, { formatApiErrorDetail, formatMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, FileText, Wallet } from "lucide-react";

const empty = {
  employee_id: "",
  month: new Date().toISOString().slice(0, 7),
  basic: 0, hra: 0, allowances: 0, deductions: 0, bonus: 0,
  currency: "INR",
  notes: "",
};

export default function Payroll() {
  const [employees, setEmployees] = useState([]);
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/payslips");
      setList(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/employees");
      setEmployees(data);
    })();
    load();
  }, []);

  const onEmployeeChange = (v) => {
    const emp = employees.find((e) => e.id === v);
    setForm({
      ...form,
      employee_id: v,
      basic: emp?.salary ? Number(emp.salary) * 0.5 : 0,
      hra: emp?.salary ? Number(emp.salary) * 0.2 : 0,
      allowances: emp?.salary ? Number(emp.salary) * 0.3 : 0,
    });
  };

  const save = async () => {
    if (!form.employee_id) return toast.error("Select an employee");
    setSaving(true);
    try {
      await api.post("/payslips", {
        ...form,
        basic: Number(form.basic) || 0,
        hra: Number(form.hra) || 0,
        allowances: Number(form.allowances) || 0,
        deductions: Number(form.deductions) || 0,
        bonus: Number(form.bonus) || 0,
      });
      toast.success("Payslip generated");
      setOpen(false);
      setForm(empty);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const empMap = Object.fromEntries(employees.map((e) => [e.id, e]));

  const downloadPdf = async (id) => {
    try {
      const res = await api.get(`/payslips/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (e) {
      toast.error("Failed to fetch PDF");
    }
  };

  const gross = Number(form.basic) + Number(form.hra) + Number(form.allowances) + Number(form.bonus);
  const net = gross - Number(form.deductions);

  return (
    <div className="space-y-6" data-testid="payroll-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] tracking-widest uppercase text-zinc-500 mb-2">Compensation</div>
          <h1 className="font-heading text-4xl font-black tracking-tight">Payroll</h1>
          <p className="text-sm text-zinc-500 mt-1">Generate and archive monthly payslips.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-payslip-btn" className="btn-pressable rounded-sm h-11 bg-[#F97316] hover:bg-[#EA580C] text-white font-mono uppercase tracking-widest text-xs px-5">
              <Plus className="h-4 w-4 mr-2" /> Generate Payslip
            </Button>
          </DialogTrigger>
          <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto rounded-sm p-6">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl tracking-tight">Generate payslip</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
              <Field label="Employee">
                <Select value={form.employee_id} onValueChange={onEmployeeChange}>
                  <SelectTrigger className="rounded-sm" data-testid="ps-emp"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {employees.map((e) => (
                      <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Month">
                <Input type="month" value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })} className="rounded-sm font-mono" />
              </Field>
              <Field label="Currency">
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["INR", "USD", "AED", "EUR", "GBP"].map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <div />
              <Field label="Basic"><Input type="number" value={form.basic} onChange={(e) => setForm({ ...form, basic: e.target.value })} className="rounded-sm font-mono" /></Field>
              <Field label="HRA"><Input type="number" value={form.hra} onChange={(e) => setForm({ ...form, hra: e.target.value })} className="rounded-sm font-mono" /></Field>
              <Field label="Allowances"><Input type="number" value={form.allowances} onChange={(e) => setForm({ ...form, allowances: e.target.value })} className="rounded-sm font-mono" /></Field>
              <Field label="Bonus"><Input type="number" value={form.bonus} onChange={(e) => setForm({ ...form, bonus: e.target.value })} className="rounded-sm font-mono" /></Field>
              <Field label="Deductions"><Input type="number" value={form.deductions} onChange={(e) => setForm({ ...form, deductions: e.target.value })} className="rounded-sm font-mono" /></Field>
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-widest font-mono text-zinc-500">Net pay</Label>
                <div className="h-10 px-3 rounded-sm bg-[#F97316] text-white flex items-center justify-end font-mono font-semibold">
                  {formatMoney(net, form.currency)}
                </div>
              </div>
              <div className="md:col-span-2">
                <Field label="Notes">
                  <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm" />
                </Field>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
              <Button onClick={save} disabled={saving} data-testid="ps-save" className="btn-pressable rounded-sm bg-[#09090B] hover:bg-zinc-800 text-white font-mono uppercase tracking-widest text-xs">
                {saving ? "Generating…" : "Generate"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm">
        <Table>
          <TableHeader>
            <TableRow className="bg-zinc-50 hover:bg-zinc-50">
              <TableHead className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Payslip</TableHead>
              <TableHead className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Employee</TableHead>
              <TableHead className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Month</TableHead>
              <TableHead className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 text-right">Gross</TableHead>
              <TableHead className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 text-right">Deductions</TableHead>
              <TableHead className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 text-right">Net</TableHead>
              <TableHead className="text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-16 text-sm text-zinc-500">
                  <Wallet className="h-8 w-8 mx-auto mb-3 text-zinc-300" />
                  No payslips yet.
                </TableCell>
              </TableRow>
            )}
            {list.map((p) => (
              <TableRow key={p.id} className="hover:bg-zinc-50" data-testid={`ps-row-${p.id}`}>
                <TableCell className="font-mono text-xs">{p.payslip_no}</TableCell>
                <TableCell className="text-sm">{empMap[p.employee_id]?.full_name || p.employee_id}</TableCell>
                <TableCell className="font-mono text-xs">{p.month}</TableCell>
                <TableCell className="text-right font-mono text-sm">{formatMoney(p.gross, p.currency)}</TableCell>
                <TableCell className="text-right font-mono text-sm">{formatMoney(p.deductions, p.currency)}</TableCell>
                <TableCell className="text-right font-mono text-sm font-semibold">{formatMoney(p.net, p.currency)}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => downloadPdf(p.id)} data-testid={`ps-pdf-${p.id}`}>
                    <FileText className="h-3.5 w-3.5 mr-1" /> <span className="text-xs font-mono uppercase tracking-widest">PDF</span>
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase tracking-widest font-mono text-zinc-500">{label}</Label>
      {children}
    </div>
  );
}
